import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/compat/router';
import { getLivePreviewConfig } from 'src/utils/module-federation/get-live-preview-config';
import useLazyRemoteModule from 'src/utils/module-federation/use-lazy-remote-module';
import type { CheckoutModuleRegistry } from './checkout-theme-contract';
import { useCheckoutThemeManifestDecision } from './use-checkout-theme-manifest-decision';
import { isStorefrontCheckoutThemeRoute } from './checkout-theme-routes';
import { useStorefrontCheckoutDispensary } from './use-storefront-checkout-dispensary';

export type RemoteCheckoutTheme = Partial<Pick<CheckoutModuleRegistry, 'CheckoutHeader'>> &
  Pick<CheckoutModuleRegistry, 'CheckoutFooter'> & {
    decision: 'native' | 'pending' | 'remote';
    isLoading: boolean;
    loadError: string | null;
  };

/**
 * Discovers and loads the dedicated Ecommerce Pro storefront-checkout theme module.
 * The generic commerce loader is hard-skipped on this route, so checkout owns the
 * only active Module Federation loader.
 */
export function useRemoteCheckoutTheme(): RemoteCheckoutTheme {
  const router = useRouter();
  const {
    dispensary,
    error: dispensaryError,
    loading: dispensaryLoading,
    routeCName,
  } = useStorefrontCheckoutDispensary({ allowMatchingCart: false });
  const isStorefrontCheckoutPage = isStorefrontCheckoutThemeRoute(router?.route);
  const rawPath = typeof router?.asPath === 'string' ? router.asPath : '';
  const pathWithoutHash = rawPath.split('#')[0];
  const currentSearchString = pathWithoutHash.includes('?') ? pathWithoutHash.split('?')[1] : '';
  const [livePreviewConfig, setLivePreviewConfig] = useState(() => getLivePreviewConfig(currentSearchString));

  useEffect(() => {
    const nextConfig = getLivePreviewConfig(currentSearchString);
    if (nextConfig) setLivePreviewConfig(nextConfig);
  }, [currentSearchString]);

  const providerResolutionPending =
    isStorefrontCheckoutPage &&
    !livePreviewConfig &&
    !dispensaryError &&
    (!routeCName || (dispensaryLoading && !dispensary));
  const customizationProvider = isStorefrontCheckoutPage
    ? livePreviewConfig ?? dispensary?.remoteExtensions?.theme
    : null;
  const checkoutThemeProvider =
    customizationProvider?.manifestUrl && customizationProvider.modulePath && customizationProvider.scope
      ? {
          manifestUrl: customizationProvider.manifestUrl,
          modulePath: customizationProvider.modulePath,
          scope: customizationProvider.scope,
        }
      : null;
  const manifestDecision = useCheckoutThemeManifestDecision(
    checkoutThemeProvider
      ? { manifestUrl: checkoutThemeProvider.manifestUrl, modulePath: checkoutThemeProvider.modulePath }
      : null
  );
  let decision: RemoteCheckoutTheme['decision'] = manifestDecision.status;
  if (!isStorefrontCheckoutPage) {
    decision = 'native';
  } else if (providerResolutionPending) {
    decision = 'pending';
  }
  const checkoutModulePath = manifestDecision.status === 'remote' ? manifestDecision.checkoutModulePath : '';
  const skip = typeof window === 'undefined' || decision !== 'remote';
  const providerKey =
    decision === 'remote'
      ? `${checkoutThemeProvider?.scope ?? ''}:${checkoutModulePath}:${checkoutThemeProvider?.manifestUrl ?? ''}`
      : '';
  const providerKeyRef = useRef(providerKey);
  providerKeyRef.current = providerKey;
  const [settledProviderKey, setSettledProviderKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailure, setLoadFailure] = useState<{ error: string; providerKey: string } | null>(null);

  const handleLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
    if (!loading) setSettledProviderKey(providerKeyRef.current);
  }, []);
  const handleLoadFail = useCallback(
    (error: string) => setLoadFailure({ error, providerKey: providerKeyRef.current }),
    []
  );

  const remoteThemeModule = useLazyRemoteModule<{
    default?: CheckoutModuleRegistry;
  }>({
    manifestUrl: checkoutThemeProvider?.manifestUrl ?? '',
    modulePath: checkoutModulePath,
    scope: checkoutThemeProvider?.scope ?? '',
    fallback: { default: undefined },
    onLoadFail: handleLoadFail,
    onLoading: handleLoading,
    skip,
  });

  const loadError = loadFailure?.providerKey === providerKey ? loadFailure.error : null;
  const modulePending = decision === 'remote' && !loadError && (isLoading || settledProviderKey !== providerKey);

  return {
    CheckoutHeader: modulePending || loadError ? undefined : remoteThemeModule.default?.CheckoutHeader,
    CheckoutFooter: modulePending || loadError ? undefined : remoteThemeModule.default?.CheckoutFooter,
    decision,
    isLoading: modulePending,
    loadError,
  };
}
